import React, { FC, useState, useEffect } from 'react'
import { useAlert } from 'react-alert'
import { useDispatch } from 'react-redux'

import 'react-image-crop/dist/ReactCrop.css'
import { fetchProfile, updateProfileImage } from '../../../states/containers/setting/profileSlice'
import Modal from './Modal'
import { useTranslation } from 'react-i18next'
import { updateCommonPart } from '../../../states/containers/common/commonPartSlice'
import ReactCrop from 'react-image-crop'

interface Props {
  isOpen: boolean
  closeCallback: () => void
  profileImageProp: string | ArrayBuffer | null
}

interface Crop {
  unit: "%" | "px",
  width: number,
  height: number,
  x: number,
  y: number,
}

const ProfileImageModal: FC<Props> = (props) => {
  const { isOpen, closeCallback, profileImageProp } = props
  const dispatch = useDispatch()
  const alert = useAlert()
  const { t } = useTranslation()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [profileImageOrg, setProfileImageOrg] = useState<any>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [profileImage, setProfileImage] = useState<any>(null)
  const [profileImageSrc, setProfileImageSrc] = useState<HTMLImageElement | null>(null)

  const [cropParam, setCropParam] = useState<Crop | null>(null)

  const getCroppedImg = (image: HTMLImageElement | null, crop: Crop) => {
    if (!image) return
    const canvas = document.createElement('canvas')
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    const ctx = canvas.getContext('2d')

    canvas.width = 100
    canvas.height = 100

    if (!ctx) return
    ctx.imageSmoothingQuality = 'high'

    ctx.fillStyle = "#FFFFFF"
    ctx.fillRect(0, 0, 100, 100)
    if (crop.unit === "px") {
      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        100,
        100,
      )
    } else {
      ctx.drawImage(
        image,
        (image.width / 100) * crop.x * scaleX,
        (image.height / 100) * crop.y * scaleY,
        (image.width / 100) * crop.width * scaleX,
        (image.height / 100) * crop.height * scaleY,
        0,
        0,
        100,
        100,
      )
    }

    const base64Image = canvas.toDataURL('image/jpeg')
    setProfileImage(base64Image)
  }

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const image = e.currentTarget
    setProfileImageSrc(image)
    const imageSize = image.naturalWidth < image.naturalHeight ? image.naturalWidth : image.naturalHeight
    getCroppedImg(image, {
      unit: '%',
      width: (imageSize / image.naturalWidth) * 80,
      height: (imageSize / image.naturalHeight) * 80,
      x: (100 - ((imageSize / image.naturalWidth) * 80)) / 2,
      y: (100 - ((imageSize / image.naturalHeight) * 80)) / 2,
    })
  }

  const onCropComplete = (crop: Crop) => {
    if (crop.width === 0 || crop.height === 0) {
      setProfileImage(null)
      return
    }
    getCroppedImg(profileImageSrc, crop)
  }

  const onCropChange = (crop: Crop) => {
    setCropParam(crop)
  }

  const closeModal = () => {
    closeCallback()
    setProfileImageOrg(null)
    setProfileImage(null)
    setProfileImageSrc(null)
    setCropParam(null)
  }

  const handleProfileImageSubmit = () => {
    dispatch(updateProfileImage(
      { image: profileImage },
      () => {
        alert.success(t('setting.profile.image.success'))
        dispatch(fetchProfile())
        dispatch(updateCommonPart())
        closeModal()
      },
      () => {
        alert.error(t('setting.profile.image.error'))
      },
    ))
  }

  const setCrop = (image: HTMLImageElement) => {
    setTimeout(
      () => {
        const imageSize = image.naturalWidth < image.naturalHeight ? image.naturalWidth : image.naturalHeight
        setCropParam({
          unit: '%',
          width: (imageSize / image.naturalWidth) * 80,
          height: (imageSize / image.naturalHeight) * 80,
          x: (100 - ((imageSize / image.naturalWidth) * 80)) / 2,
          y: (100 - ((imageSize / image.naturalHeight) * 80)) / 2,
        })
      },
      100,
    )
  }

  useEffect(() => {
    if (!profileImageProp) return
    const image = new Image()
    image.src = profileImageProp as string
    setProfileImageOrg(profileImageProp)
    setCrop(image)
  }, [profileImageProp])

  return (
    <Modal
      show={isOpen && !!cropParam}
      wrapperClass="modal_on_modal"
      closeCallback={closeModal}
      closeable
    >
      <>
        <h3 className="modal-title">{t('setting.profile.image.title')}</h3>
        <div className="modal-body">
          <>
            {
              profileImageOrg && cropParam && (
                <ReactCrop
                  crop={cropParam}
                  ruleOfThirds
                  // onImageLoaded={onImageLoaded}
                  onComplete={onCropComplete}
                  onChange={onCropChange}
                  minWidth={100}
                  minHeight={100}
                  aspect={1}
                >
                  <img
                    src={profileImageOrg}
                    alt=""
                    onLoad={onImageLoad}
                  />
                </ReactCrop>
              )
            }
            <div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleProfileImageSubmit()}
                disabled={!profileImage}
              >
                {t('setting.profile.button.create')}
              </button>
            </div>
          </>
        </div>
      </>
    </Modal>
  )
}

export default ProfileImageModal
